import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Trash2, ArrowUp, ArrowDown, GripVertical, Plus, Settings2, ImagePlus, LayoutGrid, Monitor, Tablet, Smartphone, Copy, Eye, EyeOff, FileText, Palette, Shapes, Type, Image as ImageIcon, Images, CircleHelp, Package, FolderOpen, Star, ListOrdered, Sparkles, MessageSquare, Minus, MapPin, Video, Code, Coins, Newspaper, MessagesSquare, Table, Box, ShoppingCart, ShoppingBag, ClipboardList, Megaphone, PanelTop, PanelBottom, Store, Menu as MenuIcon, Search, MousePointerClick, User, SunMoon, Phone, Share2, List as ListIcon, Contact, Mail, Copyright, Rows3, Columns3, MoreHorizontal, Maximize2, Check, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { STORAGE_BASE } from '../config';
import { ICONS, ICON_NAMES } from '../sections/WidgetIcons';
import { WidgetShell, WidgetStyleSection, isStyleEmpty, widgetStyleObj, frameStyle } from './widgetStyle';
import { AiImageButton } from './AiImage';
import { AdminModal, WidgetSettingsModal, CardMenu } from './widgetModal';
import { TemplateSelect, ListTplEditor } from './listTplFields';
import { ProductCard, PostCard } from '../sections/ListTemplate';
import { FCheck } from './builderFields';
import {
  WidgetText, WidgetImage, WidgetSlider, WidgetFaq, WidgetIcon, WidgetDivider,
  WidgetMap, WidgetHtml, WidgetVideo, WidgetIconText, WidgetPrice, WidgetPostList,
  WidgetPostMeta, WidgetPostComments, WidgetTable, WidgetCart, WidgetForm,
  WidgetProductScroll, WidgetCategoryStrip, WidgetDealRow,
} from '../sections/WidgetRender';
import SectionHero from '../sections/SectionHero';
import SectionFeatured from '../sections/SectionFeatured';
import SectionCategories from '../sections/SectionCategories';
import SectionProducts from '../sections/SectionProducts';
import SectionSlider from '../sections/SectionSlider';
import SectionFaq from '../sections/SectionFaq';
import SectionHowToOrder from '../sections/SectionHowToOrder';

/* ---------- widget palette groups (so admins don't get lost) ---------- */
export const WIDGET_GROUPS = [
  { title: 'محتوای متنی', Icon: Type, types: ['text', 'hero', 'faq', 'icon', 'icon_text', 'divider', 'table'] },
  { title: 'فروشگاه', Icon: ShoppingCart, types: ['product_list', 'featured', 'categories', 'rates', 'cart_widget', 'how_to_order', 'shop_actions', 'form'] },
  { title: 'اسلایدرها و ویترین‌ها', Icon: Images, types: ['slider_pro', 'product_scroll', 'category_strip', 'deal_row'] },
  { title: 'رسانه', Icon: Images, types: ['image', 'slider', 'video', 'map', 'html'] },
  { title: 'وبلاگ', Icon: Newspaper, types: ['post_list', 'post_meta', 'post_comments'] },
  { title: 'هدر', Icon: PanelTop, types: ['announcement', 'topbar', 'brand', 'menu', 'mega_menu', 'search', 'header_button', 'account_button', 'theme_toggle', 'contact', 'socials'] },
  { title: 'فوتر', Icon: PanelBottom, types: ['brand_about', 'link_list', 'contact_info', 'newsletter', 'copyright'] },
  { title: 'پیشرفته', Icon: Shapes, types: ['container'] },
];

/* ---------- font-icon for every widget type (no emoji in admin) ---------- */
const TYPE_ICONS = {
  text: Type, image: ImageIcon, slider: Images, slider_pro: Images, product_scroll: Package, category_strip: FolderOpen, deal_row: Star, faq: CircleHelp,
  product_list: Package, categories: FolderOpen, hero: Megaphone, featured: Star,
  how_to_order: ListOrdered, icon: Sparkles, icon_text: MessageSquare, divider: Minus,
  map: MapPin, video: Video, html: Code, rates: Coins,
  post_list: Newspaper, post_meta: FileText, post_comments: MessagesSquare,
  table: Table, container: Box, cart_widget: ShoppingBag, form: ClipboardList,
  announcement: Megaphone, topbar: PanelTop, brand: Store, menu: MenuIcon,
  mega_menu: LayoutGrid, search: Search, header_button: MousePointerClick,
  account_button: User, theme_toggle: SunMoon, contact: Phone, socials: Share2,
  brand_about: Store, link_list: ListIcon, contact_info: Contact,
  newsletter: Mail, copyright: Copyright, shop_actions: ShoppingCart,
};
export function TypeIcon({ type, size = 18 }) {
  const C = TYPE_ICONS[type] || Shapes;
  return <C size={size} style={{ flexShrink: 0 }} />;
}

/* ---------- mock data for live previews (no network needed) ---------- */
const MOCK_PRODUCT = {
  id: 1, name: 'انگشتر نقره نمونه', slug: 'sample', price: 1250000, stock: 5,
  customization_data: { base_price: 1250000 }, image_path: null, cover: null, media: [],
  description: 'توضیحات نمونه محصول برای پیش‌نمایش.',
};
const MOCK_POST = {
  id: 1, title: 'عنوان نمونه مطلب', slug: 'sample', excerpt: 'خلاصه نمونه مطلب برای پیش‌نمایش کارت وبلاگ.',
  cover: null, published_at: new Date().toISOString(),
};
const MOCK_DATA = {
  products: [MOCK_PRODUCT,
    { ...MOCK_PRODUCT, id: 2, name: 'گردنبند نقره نمونه', price: 890000, customization_data: { base_price: 890000 } },
    { ...MOCK_PRODUCT, id: 3, name: 'دستبند نقره نمونه', price: 640000, customization_data: { base_price: 640000 } }],
  categories: [
    { id: 1, name: 'انگشتر', slug: 'rings', image_path: null, products: [] },
    { id: 2, name: 'گردنبند', slug: 'necklaces', image_path: null, products: [] },
    { id: 3, name: 'دستبند', slug: 'bracelets', image_path: null, products: [] },
    { id: 4, name: 'گوشواره', slug: 'earrings', image_path: null, products: [] },
  ],
  faqs: [{ question: 'پرسش نمونه؟', answer: 'پاسخ نمونه برای پیش‌نمایش.' }],
};
const MOCK_CTX = { ...MOCK_DATA, post: MOCK_POST, product: MOCK_PRODUCT };
export { MOCK_CTX as MOCK_PREVIEW_CTX };

const PREVIEW_HINT = 'هنوز محتوایی برای پیش‌نمایش وارد نشده — فیلدهای بالا را پر کنید.';

/* Live preview of ANY page widget with its current settings.
 * Data-driven widgets use mock items; self-fetching ones (rates,
 * post_list) hit the real API so the preview is truly live. */
export function WidgetLivePreview({ type, settings = {}, bare }) {
  const s = settings;
  // Same frame + unified-style wrap as the storefront (renderWidget in HomePage),
  // so the studio preview matches the live site pixel-behavior.
  const wrap = children => {
    const fs = type === 'text' ? undefined : frameStyle(s.frame);
    const us = widgetStyleObj(s.style);
    const st = { ...fs, ...us };
    if (!st || !Object.keys(st).length) return children;
    return <div style={st}>{children}</div>;
  };
  // bare: clean render exactly like the site-builder studio (no dashed box/label).
  if (bare) {
    switch (type) {
      case 'text': return s.content ? wrap(<WidgetText settings={s} />) : null;
      case 'image': return s.image ? wrap(<WidgetImage settings={s} />) : null;
      case 'slider': return wrap((s.slides && s.slides.length) ? <WidgetSlider settings={s} /> : <SectionSlider data={MOCK_DATA} settings={s} />);
      case 'faq': return wrap(<WidgetFaq settings={s} data={MOCK_DATA} />);
      case 'product_list': return wrap(<SectionProducts data={MOCK_DATA} settings={s} embedded />);
      case 'categories': return wrap(<SectionCategories data={MOCK_DATA} settings={s} embedded />);
      case 'hero': return wrap(<SectionHero data={MOCK_DATA} settings={s} embedded />);
      case 'featured': return wrap(<SectionFeatured data={MOCK_DATA} settings={s} embedded />);
      case 'how_to_order': return wrap(<SectionHowToOrder data={MOCK_DATA} settings={s} embedded />);
      case 'icon': return wrap(<WidgetIcon settings={s} />);
      case 'icon_text': return wrap(<WidgetIconText settings={s} />);
      case 'divider': return wrap(<WidgetDivider settings={s} />);
      case 'map': return (s.embedUrl || s.address) ? wrap(<WidgetMap settings={s} />) : null;
      case 'video': return s.video ? wrap(<WidgetVideo settings={s} />) : null;
      case 'html': return s.html ? wrap(<WidgetHtml settings={s} />) : null;
      case 'rates': return wrap(<WidgetPrice settings={s} />);
      case 'post_list': return wrap(<WidgetPostList settings={s} />);
      case 'post_meta': return wrap(<WidgetPostMeta settings={s} ctx={MOCK_CTX} />);
      case 'post_comments': return wrap(<WidgetPostComments settings={s} ctx={MOCK_CTX} />);
      case 'table': return (s.headers || s.rows_text) ? wrap(<WidgetTable settings={s} />) : null;
      case 'product_scroll': return wrap(<WidgetProductScroll settings={s} data={MOCK_DATA} />);
      case 'category_strip': return wrap(<WidgetCategoryStrip settings={s} data={MOCK_DATA} />);
      case 'deal_row': return wrap(<WidgetDealRow settings={s} data={MOCK_DATA} />);
      case 'cart_widget': return wrap(<WidgetCart settings={s} />);
      case 'form': return s.form_id ? wrap(<WidgetForm settings={s} />) : null;
      case 'container': {
        const n = (s.innerRows || []).length;
        return n ? wrap(<div className="text-xs text-muted-foreground">کانتینر · {Number(n).toLocaleString('fa-IR')} ردیف داخلی</div>) : null;
      }
      default: return null;
    }
  }
  const box = (children, raw) => (
    <div style={{ border: '1px dashed #D6A93B', borderRadius: 12, padding: 14, background: '#FFFBEB', overflow: 'hidden' }}>
      <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#92400E', marginBottom: 10, letterSpacing: 0.3, display: 'flex', alignItems: 'center', gap: 5 }}><Eye size={12} /> پیش‌نمایش زنده (با تنظیمات فعلی)</div>
      {raw ? children : wrap(children)}
    </div>
  );
  const hint = box(<div style={{ fontSize: '0.78rem', color: '#92400E', textAlign: 'center', padding: 8 }}>{PREVIEW_HINT}</div>);
  switch (type) {
    case 'text': return s.content ? box(<WidgetText settings={s} />) : hint;
    case 'image': return s.image ? box(<WidgetImage settings={s} />) : hint;
    case 'slider':
      return (s.slides && s.slides.length) ? box(<WidgetSlider settings={s} />)
        : box(<SectionSlider data={MOCK_DATA} settings={s} />);
    case 'faq': return box(<WidgetFaq settings={s} data={MOCK_DATA} />);
    case 'product_list': return box(<SectionProducts data={MOCK_DATA} settings={s} embedded />);
    case 'categories': return box(<SectionCategories data={MOCK_DATA} settings={s} embedded />);
    case 'hero': return box(<SectionHero data={MOCK_DATA} settings={s} embedded />);
    case 'featured': return box(<SectionFeatured data={MOCK_DATA} settings={s} embedded />);
    case 'how_to_order': return box(<SectionHowToOrder data={MOCK_DATA} settings={s} embedded />);
    case 'icon': return box(<WidgetIcon settings={s} />);
    case 'icon_text': return box(<WidgetIconText settings={s} />);
    case 'divider': return box(<WidgetDivider settings={s} />);
    case 'map': return (s.embedUrl || s.address) ? box(<WidgetMap settings={s} />) : hint;
    case 'video': return s.video ? box(<WidgetVideo settings={s} />) : hint;
    case 'html': return s.html ? box(<WidgetHtml settings={s} />) : hint;
    case 'rates': return box(<WidgetPrice settings={s} />);
    case 'post_list': return box(<WidgetPostList settings={s} />);
    case 'post_meta': return box(<WidgetPostMeta settings={s} ctx={MOCK_CTX} />);
    case 'post_comments': return box(<WidgetPostComments settings={s} ctx={MOCK_CTX} />);
    case 'table': return (s.headers || s.rows_text) ? box(<WidgetTable settings={s} />) : hint;
    case 'product_scroll': return box(<WidgetProductScroll settings={s} data={MOCK_DATA} />);
    case 'category_strip': return box(<WidgetCategoryStrip settings={s} data={MOCK_DATA} />);
    case 'deal_row': return box(<WidgetDealRow settings={s} data={MOCK_DATA} />);
    case 'cart_widget': return box(<WidgetCart settings={s} />);
    case 'form': return s.form_id ? box(<WidgetForm settings={s} />) : hint;
    case 'container': {
      const n = (s.innerRows || []).length;
      return box(<div style={{ fontSize: '0.8rem', color: '#92400E', textAlign: 'center', padding: 8 }}>کانتینر شامل {Number(n || 0).toLocaleString('fa-IR')} ردیف داخلی است — ردیف‌های داخل را جداگانه باز کنید.</div>);
    }
    default: return null;
  }
}

/* Searchable, grouped widget picker — shared by page + header/footer editors */
export function WidgetPicker({ types, onPick, onClose }) {
  const [q, setQ] = useState('');
  const needle = (q || '').trim();
  const match = t => !needle || t.label.includes(needle) || (t.desc || '').includes(needle);
  const groups = WIDGET_GROUPS
    .map(g => ({ ...g, items: (types || []).filter(t => g.types.includes(t.type) && match(t)) }))
    .filter(g => g.items.length);
  const known = new Set(WIDGET_GROUPS.flatMap(g => g.types));
  const rest = (types || []).filter(t => !known.has(t.type) && match(t));
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div onClick={onClose} role="dialog" aria-modal="true"
      style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(12,10,9,0.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, direction: 'rtl', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 680, maxHeight: '86vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ padding: '18px 22px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <div style={{ fontWeight: 800, fontSize: '1rem', color: '#18181B', flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}><Shapes size={20} /> انتخاب ویجت</div>
            <button type="button" onClick={onClose} aria-label="بستن"
              style={{ width: 34, height: 34, borderRadius: '50%', border: '1px solid #E4E4E7', background: '#fff', color: '#52525B', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><X size={16} /></button>
          </div>
          <p style={{ fontSize: '0.76rem', color: '#A1A1AA', margin: '0 0 14px' }}>ویجت موردنظر را جستجو یا از دسته‌ها انتخاب کنید؛ با کلیک اضافه می‌شود.</p>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="جستجوی ویجت... (مثلاً: اسلایدر، جدول، سبد)" autoFocus
            style={{ width: '100%', padding: '11px 16px', borderRadius: 12, border: '1.5px solid #E4E4E7', fontSize: '0.85rem', outline: 'none', background: '#FAFAFA', fontFamily: 'inherit', boxSizing: 'border-box' }} />
        </div>
        <div style={{ overflowY: 'auto', padding: '16px 22px 22px', display: 'flex', flexDirection: 'column', gap: 20 }}>
      {groups.map(g => (
        <div key={g.title}>
          <div style={{ fontSize: '0.74rem', fontWeight: 800, color: '#52525B', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 7 }}>{g.Icon && <g.Icon size={15} />}{g.title}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
            {g.items.map(t => (
              <button key={t.type} type="button" onClick={() => onPick(t.type)}
                title={t.desc}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px', borderRadius: 12, border: '1px solid #E4E4E7', background: '#fff', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'right', transition: 'border-color 0.15s, box-shadow 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#18181B'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#E4E4E7'; e.currentTarget.style.boxShadow = 'none'; }}>
                <span style={{ display: 'inline-flex', color: '#52525B' }}><TypeIcon type={t.type} size={22} /></span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#18181B' }}>{t.label}</span>
                  <span style={{ display: 'block', fontSize: '0.68rem', color: '#A1A1AA', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.desc}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      ))}
      {!!rest.length && (
        <div>
          <div style={{ fontSize: '0.74rem', fontWeight: 800, color: '#52525B', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 7 }}><Shapes size={15} /> سایر</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
            {rest.map(t => (
              <button key={t.type} type="button" onClick={() => onPick(t.type)}
                title={t.desc}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px', borderRadius: 12, border: '1px solid #E4E4E7', background: '#fff', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'right' }}>
                <span style={{ display: 'inline-flex', color: '#52525B' }}><TypeIcon type={t.type} size={22} /></span>
                <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#18181B' }}>{t.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {!groups.length && !rest.length && (
        <div style={{ fontSize: '0.85rem', color: '#A1A1AA', textAlign: 'center', padding: 20 }}>ویجتی با این نام پیدا نشد.</div>
      )}
        </div>
      </div>
    </div>
  );
}

export const uid = () => Math.random().toString(36).slice(2, 10);
const resolveSrc = p => (!p ? '' : (/^https?:\/\//.test(p) ? p : `${STORAGE_BASE}/${String(p).replace(/^\/+/, '')}`));

export const WIDGET_TYPES = [
  { type: 'text', label: 'متن', desc: 'متن دلخواه با استایل کامل' },
  { type: 'image', label: 'تصویر', desc: 'تصویر با لینک دلخواه' },
  { type: 'slider', label: 'اسلایدر تصاویر', desc: 'اسلایدهای تصویری' },
  { type: 'faq', label: 'سوالات متداول', desc: 'آکاردئون پرسش و پاسخ' },
  { type: 'product_list', label: 'لیست محصولات', desc: 'نمایش محصولات فروشگاه' },
  { type: 'categories', label: 'دسته‌بندی‌ها', desc: 'شبکه دسته‌بندی محصولات' },
  { type: 'hero', label: 'هریو (Hero)', desc: 'بنر اصلی با دکمه' },
  { type: 'featured', label: 'پیشنهادات ویژه', desc: 'محصولات ویژه' },
  { type: 'how_to_order', label: 'روش سفارش', desc: 'مراحل سفارش' },
  { type: 'icon', label: 'آیکون', desc: 'آیکون با رنگ و لینک دلخواه' },
  { type: 'icon_text', label: 'متن با آیکون', desc: 'آیکون کنار عنوان و متن' },
  { type: 'divider', label: 'جداکننده', desc: 'خط جداکننده استایل‌دار' },
  { type: 'map', label: 'نقشه', desc: 'نقشه گوگل با آدرس دلخواه' },
  { type: 'video', label: 'ویدیو', desc: 'پخش‌کننده اختصاصی' },
  { type: 'html', label: 'HTML سفارشی', desc: 'کد دلخواه خودتان' },
  { type: 'rates', label: 'نرخ ارز و نقره', desc: 'قیمت لحظه‌ای + مبدل' },
  { type: 'container', label: 'کانتینر تودرتو', desc: 'ردیف و ستون داخل ویجت' },
  { type: 'post_list', label: 'لیست مطالب وبلاگ', desc: 'کارت‌های آخرین مطالب' },
  { type: 'post_meta', label: 'مشخصات مطلب', desc: 'عنوان، کاور و تاریخ مطلب جاری' },
  { type: 'post_comments', label: 'دیدگاه‌های مطلب', desc: 'باکس نظرات مطلب جاری' },
  { type: 'table', label: 'جدول', desc: 'جدول داده با سرستون رنگی' },
  { type: 'cart_widget', label: 'سبد خرید', desc: 'نمایش سبد خرید با آدرس و پرداخت' },
  { type: 'form', label: 'فرم', desc: 'فرم طراحی‌شده در فرم‌ساز' },
  { type: 'product_scroll', label: 'ویترین افقی محصولات', desc: 'اسکرول کارت‌های محصول با تخفیف (سبک دیجی‌کالا)' },
  { type: 'slider_pro', label: 'اسلایدر ساز (استودیو اسلایدر)', desc: 'اسلایدر حرفه‌ای با ۶ سبک: کلاسیک، کاورفلو، محو شدن، نوار متحرک، استوری، بندانگشتی — محصول/دسته/اسلاید دستی' },
  { type: 'category_strip', label: 'نوار دسته‌بندی‌ها', desc: 'چیپ‌های اسکرولی دسته‌بندی با تصویر (سبک دیجی‌کالا)' },
  { type: 'deal_row', label: 'پیشنهاد ویژه', desc: 'پنل تخفیف + کارت‌های تخفیف‌دار (سبک دیجی‌کالا)' },
];

export function useIsMobile() {
  const [mobile, setMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

/* ---------- shared field styles (same tokens as products page) ---------- */
const fieldInput = { width: '100%', padding: '10px 14px', borderRadius: 12, border: '1.5px solid #E4E4E7', fontSize: '0.85rem', outline: 'none', background: '#FAFAFA', direction: 'rtl', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.2s, box-shadow 0.2s' };

/** آیا این ویجت باید در صفحه دیده شود؟ (خاموش → کلا display:none) */
export function widgetVisible(w) {
  if (!w) return false;
  if (w.enabled === false) return false;
  if ((w.type === 'container' || w.type === 'section') && (w.settings?.innerRows || []).length) {
    return (w.settings.innerRows || []).some(rowVisible);
  }
  return true;
}

/** آیا این ردیف چیزی برای نمایش دارد؟ ردیف فاصله‌انداز (بدون ویجت) می‌ماند. */
export function rowVisible(r) {
  if (!r) return false;
  if (r.enabled === false) return false;
  const ws = (r.columns || []).flatMap(c => c.widgets || []);
  if (!ws.length) return true;
  return ws.some(widgetVisible);
}

export const fieldCls = 'w-full rounded-xl border border-input bg-field px-3.5 py-2.5 text-[13px] text-foreground outline-none transition placeholder:text-muted-foreground focus:border-ring focus:ring-2 focus:ring-ring/20';
export const fieldLabelCls = 'mb-1.5 block text-right text-xs font-semibold text-muted-foreground';
const fieldLabel = { display: 'block', marginBottom: 6, fontSize: '0.78rem', fontWeight: 600, color: '#52525B', textAlign: 'right' };
const iconBtn = { width: 32, height: 32, borderRadius: 8, border: '1px solid #E4E4E7', background: '#fff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#52525B', flexShrink: 0 };
const focusRing = e => { e.target.style.borderColor = '#A1A1AA'; e.target.style.boxShadow = '0 0 0 3px rgba(24,24,27,0.06)'; };
const blurRing = e => { e.target.style.borderColor = '#E4E4E7'; e.target.style.boxShadow = 'none'; };

/* ---------- box style: per-corner radius + shadow + outer margin ---------- */
const SHADOWS = {
  sm: '0 1px 2px rgba(0,0,0,0.06)',
  md: '0 4px 12px rgba(0,0,0,0.08)',
  lg: '0 8px 30px rgba(28,25,23,0.10)',
  xl: '0 16px 48px rgba(28,25,23,0.14)',
};
const radiusCss = s => {
  const tl = s.radiusTL ?? s.radius ?? 0, tr = s.radiusTR ?? s.radius ?? 0;
  const br = s.radiusBR ?? s.radius ?? 0, bl = s.radiusBL ?? s.radius ?? 0;
  return (tl || tr || br || bl) ? `${tl}px ${tr}px ${br}px ${bl}px` : undefined;
};

const FText = ({ label, value, onChange, placeholder }) => (
  <div><label className={fieldLabelCls}>{label}</label>
    <input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className={fieldCls} /></div>
);
const FArea = ({ label, value, onChange, placeholder, rows }) => (
  <div><label className={fieldLabelCls}>{label}</label>
    <textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows || 4} className={fieldCls} style={{resize: 'vertical', lineHeight: 1.9}} /></div>
);
const FNumber = ({ label, value, onChange, min, max }) => (
  <div><label className={fieldLabelCls}>{label}</label>
    <input type="number" value={value ?? ''} onChange={e => onChange(Number(e.target.value))} min={min} max={max} className={fieldCls} /></div>
);
const FSelect = ({ label, value, onChange, options }) => (
  <div><label className={fieldLabelCls}>{label}</label>
    <select value={value} onChange={e => onChange(e.target.value)} className={fieldCls} style={{cursor: 'pointer'}}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select></div>
);
const FColor = ({ label, value, onChange }) => (
  <div><label className={fieldLabelCls}>{label}</label>
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(value || '') ? value : '#18181B'} onChange={e => onChange(e.target.value)}
        style={{ width: 44, height: 38, padding: 2, borderRadius: 10, border: '1.5px solid #E4E4E7', background: '#FAFAFA', cursor: 'pointer', flexShrink: 0 }} />
      <input value={value || ''} onChange={e => onChange(e.target.value)} placeholder="#18181B یا transparent" className={fieldCls} dir="ltr" />
    </div></div>
);

/* ---------- image field with upload ---------- */
function FImage({ label, value, onChange, accept, kind }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  // Downscale large images in browser so uploads stay small and fast.
  // Files ≤1.5MB go up untouched (keeps PNG transparency, GIF intact).
  const prepare = file => new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith('image/') || file.size <= 1.5 * 1024 * 1024) return resolve(file);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);
      const keepPng = file.type === 'image/png';
      canvas.toBlob(
        b => (b ? resolve(new File([b], file.name.replace(/\.\w+$/, keepPng ? '.png' : '.jpg'), { type: keepPng ? 'image/png' : 'image/jpeg' })) : reject(new Error('compress'))),
        keepPng ? 'image/png' : 'image/jpeg',
        0.82,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode')); };
    img.src = url;
  });
  const upload = async file => {
    if (!file) return;
    setUploading(true);
    try {
      const ready = await prepare(file);
      const fd = new FormData();
      fd.append('file', ready);
      const r = await axios.post('/media/upload', fd);
      onChange(r.data.path);
      toast.success('تصویر آپلود شد.', { autoClose: 5000 });
    } catch (err) {
      toast.error('خطا در آپلود: ' + (err.response?.data?.message || err.message), { autoClose: 5000 });
    }
    setUploading(false);
  };
  return (
    <div>
      <label className={fieldLabelCls}>{label}</label>
      <div style={{ display: 'flex', gap: 8 }}>
        <input value={value || ''} onChange={e => onChange(e.target.value)} placeholder="آدرس تصویر (URL) یا آپلود" className={fieldCls} style={{direction: 'ltr', textAlign: 'left'}} />
        <button onClick={() => fileRef.current?.click()} disabled={uploading}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#18181B', color: '#fff', border: 'none', padding: '0 16px', borderRadius: 12, fontSize: '0.8rem', fontWeight: 600, cursor: uploading ? 'default' : 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', opacity: uploading ? 0.6 : 1 }}>
          <ImagePlus size={15} /> {uploading ? '...' : 'آپلود'}
        </button>
        <AiImageButton onPick={onChange} resolveSrc={resolveSrc} />
        <input ref={fileRef} type="file" accept={accept || 'image/*'} style={{ display: 'none' }} onChange={e => { upload(e.target.files?.[0]); e.target.value = ''; }} />
      </div>
      {value && (
        <div style={{ marginTop: 8, borderRadius: 12, overflow: 'hidden', border: '1px solid #E4E4E7', maxWidth: 280 }}>
          {kind === 'video' ? (
            <video src={resolveSrc(value)} controls style={{ width: '100%', display: 'block', maxHeight: 160, background: '#000' }} />
          ) : (
            <img src={resolveSrc(value)} alt="" style={{ width: '100%', display: 'block', maxHeight: 160, objectFit: 'cover' }} />
          )}
        </div>
      )}
    </div>
  );
}

/* ---------- per-widget settings editors ---------- */

/* ---------- four-side spacing (padding/margin per side) ---------- */
function SidesFields({ v = {}, onChange, prefix, title }) {
  const set = (k, val) => onChange({ ...v, [`${prefix}${k}`]: val });
  const side = (k, label) => (
    <div><label className={fieldLabelCls}>{label}</label>
      <input type="number" value={v[`${prefix}${k}`] ?? ''} onChange={e => set(k, Number(e.target.value))} min={0} max={120}
        className={fieldCls} /></div>
  );
  return (
    <div style={{ gridColumn: '1 / -1' }}>
      <div className={fieldLabelCls}>{title}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
        {side('T', 'بالا')}
        {side('R', 'راست')}
        {side('B', 'پایین')}
        {side('L', 'چپ')}
      </div>
    </div>
  );
}

function BoxStyleFields({ v = {}, onChange, marginKey, marginLabel }) {
  const set = (k, val) => onChange({ ...v, [k]: val });
  const corner = (key, label) => (
    <div><label className={fieldLabelCls}>{label}</label>
      <input type="number" value={v[key] ?? v.radius ?? ''} onChange={e => set(key, Number(e.target.value))} min={0} max={80}
        className={fieldCls} /></div>
  );
  return (<>
    <div style={{ gridColumn: '1 / -1' }}>
      <div className={fieldLabelCls}>گردی هر گوشه (px)</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
        {corner('radiusTR', 'بالا راست')}
        {corner('radiusTL', 'بالا چپ')}
        {corner('radiusBR', 'پایین راست')}
        {corner('radiusBL', 'پایین چپ')}
      </div>
    </div>
    <FSelect label="سایه" value={v.shadow || ''} onChange={val => set('shadow', val)}
      options={[{ value: '', label: 'بدون سایه' }, { value: 'sm', label: 'کم' }, { value: 'md', label: 'متوسط' }, { value: 'lg', label: 'زیاد' }, { value: 'xl', label: 'خیلی زیاد' }]} />
    {marginKey && <FNumber label={marginLabel || 'فاصله بیرونی (px)'} value={v[marginKey]} onChange={val => set(marginKey, val)} min={0} max={80} />}
  </>);
}

/* ---------- icon picker (searchable, ~120 icons) ---------- */
function FIconPicker({ label, value, onChange }) {
  const [q, setQ] = useState('');
  const list = ICON_NAMES.filter(n => n.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 72);
  const Sel = ICONS[value] || null;
  return (
    <div style={{ gridColumn: '1 / -1' }}>
      <label className={fieldLabelCls}>{label}</label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        <span style={{ width: 42, height: 42, borderRadius: 12, border: '1.5px solid var(--input)', background: 'var(--brand)', color: 'var(--brand-foreground)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {Sel ? <Sel size={20} /> : '?'}
        </span>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="جستجوی آیکون (نام انگلیسی)..." className={fieldCls} />
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', maxHeight: 190, overflowY: 'auto', padding: 8, border: '1.5px solid var(--input)', borderRadius: 12, background: 'var(--field)' }}>
        {list.map(n => {
          const C = ICONS[n];
          const active = value === n;
          return (
            <button key={n} type="button" title={n} onClick={() => onChange(n)}
              style={{ width: 38, height: 38, borderRadius: 10, border: active ? '2px solid var(--brand)' : '1px solid var(--input)', background: active ? 'var(--brand)' : 'var(--card)', color: active ? 'var(--brand-foreground)' : 'var(--muted-foreground)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <C size={18} />
            </button>
          );
        })}
        {list.length === 0 && <span style={{ fontSize: '0.8rem', color: 'var(--muted-foreground)', padding: 8 }}>آیکونی یافت نشد.</span>}
      </div>
    </div>
  );
}

/* ---------- category selector for product_list (own fetch, no prop drilling) ---------- */
function CategorySelect({ value, onChange }) {
  const [cats, setCats] = useState([]);
  useEffect(() => {
    axios.get('/categories')
      .then(r => setCats(Array.isArray(r.data) ? r.data : (r.data?.data || [])))
      .catch(() => {});
  }, []);
  return (
    <FSelect label="دسته‌بندی" value={value} onChange={onChange}
      options={[{ value: '', label: 'همه محصولات' }, ...cats.map(c => ({ value: String(c.id), label: c.name }))]} />
  );
}
function RatesItemPicker({ value, onChange }) {
  const [items, setItems] = useState([]);
  useEffect(() => { axios.get('/rates').then(r => setItems(r.data.items || [])).catch(() => {}); }, []);
  if (!items.length) return <div style={{ fontSize: '0.75rem', color: '#A1A1AA', gridColumn: '1 / -1' }}>اقلام قیمت در «تنظیمات › نرخ نقره/ارز» ساخته می‌شوند؛ خالی = نمایش همه.</div>;
  const toggle = id => onChange((value || []).includes(id) ? (value || []).filter(v => v !== id) : [...(value || []), id]);
  return (
    <div style={{ gridColumn: '1 / -1', border: '1px solid #E4E4E7', borderRadius: 10, padding: '10px 12px' }}>
      <div style={{ fontSize: '0.78rem', color: '#78716c', marginBottom: 6 }}>نمایش فقط این اقلام (خالی = همه)</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {items.map(it => (
          <label key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', cursor: 'pointer', background: '#F4F4F5', borderRadius: 8, padding: '6px 10px' }}>
            <input type="checkbox" checked={(value || []).includes(it.id)} onChange={() => toggle(it.id)} /> {it.title}
          </label>
        ))}
      </div>
    </div>
  );
}

export function HomeWidgetContent({ type, settings, onChange }) {
  const update = (key, val) => onChange({ ...settings, [key]: val });
  const isMobileNested = useIsMobile();

  if (type === 'text') return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
      <div style={{ gridColumn: '1 / -1' }}><FArea label="متن" value={settings.content} onChange={v => update('content', v)} placeholder="متن دلخواه..." rows={4} /></div>
      <div><label className={fieldLabelCls}>ضخیم</label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: '#52525B' }}>
          <input type="checkbox" checked={!!settings.bold} onChange={e => update('bold', e.target.checked)} style={{ accentColor: '#18181B', width: 16, height: 16 }} /> متن ضخیم
        </label></div>
      <FNumber label="فاصله داخلی (px)" value={settings.padding} onChange={v => update('padding', v)} min={0} max={80} />
      <FNumber label="گردی گوشه (px)" value={settings.radius} onChange={v => update('radius', v)} min={0} max={48} />
    </div>
  );

  if (type === 'image') return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
      <div style={{ gridColumn: '1 / -1' }}><FImage label="تصویر" value={settings.image} onChange={v => update('image', v)} /></div>
      <FText label="لینک (اختیاری)" value={settings.link} onChange={v => update('link', v)} placeholder="/products یا https://..." />
      <FText label="متن جایگزین" value={settings.alt} onChange={v => update('alt', v)} placeholder="توضیح تصویر" />
      <FNumber label="عرض (٪)" value={settings.width ?? 100} onChange={v => update('width', v)} min={10} max={100} />
      <FNumber label="عرض موبایل (٪)" value={settings.widthMobile} onChange={v => update('widthMobile', v)} min={10} max={100} />
      <FNumber label="ارتفاع" value={settings.imgHeight} onChange={v => update('imgHeight', v)} min={10} max={1200} />
      <FSelect label="واحد ارتفاع" value={settings.imgHeightUnit || ''} onChange={v => update('imgHeightUnit', v)}
        options={[{ value: '', label: 'خودکار' }, { value: 'px', label: 'پیکسل' }, { value: '%', label: '٪ از عرض' }]} />
      <FNumber label="گردی گوشه (px)" value={settings.radius} onChange={v => update('radius', v)} min={0} max={48} />
      <FSelect label="تراز" value={settings.align || 'center'} onChange={v => update('align', v)}
        options={[{ value: 'right', label: 'راست' }, { value: 'center', label: 'وسط' }, { value: 'left', label: 'چپ' }]} />
    </div>
  );

  if (type === 'slider') {
    const slides = settings.slides || [];
    const setSlides = sl => update('slides', sl);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
          <FNumber label="سرعت تغییر (ms)" value={settings.interval ?? 5000} onChange={v => update('interval', v)} min={1000} max={15000} />
          <FNumber label="عرض (٪)" value={settings.width ?? 100} onChange={v => update('width', v)} min={10} max={100} />
          <FNumber label="عرض موبایل (٪)" value={settings.widthMobile} onChange={v => update('widthMobile', v)} min={10} max={100} />
          <FNumber label="ارتفاع" value={settings.height ?? 380} onChange={v => update('height', v)} min={10} max={1200} />
          <FSelect label="واحد ارتفاع" value={settings.heightUnit || 'px'} onChange={v => update('heightUnit', v)}
            options={[{ value: 'px', label: 'پیکسل' }, { value: '%', label: '٪ از عرض' }]} />
          <FNumber label="ارتفاع موبایل" value={settings.heightMobile} onChange={v => update('heightMobile', v)} min={10} max={1200} />
          <FSelect label="واحد ارتفاع موبایل" value={settings.heightMobileUnit || 'px'} onChange={v => update('heightMobileUnit', v)}
            options={[{ value: 'px', label: 'پیکسل' }, { value: '%', label: '٪ از عرض' }]} />
          <FColor label="رنگ متن اسلاید" value={settings.slideColor} onChange={v => update('slideColor', v)} />
          <FNumber label="اندازه متن اسلاید (px)" value={settings.slideSize} onChange={v => update('slideSize', v)} min={14} max={56} />
          <FNumber label="گردی گوشه (px)" value={settings.radius ?? 16} onChange={v => update('radius', v)} min={0} max={48} />
        </div>
        {slides.map((sl, i) => (
          <div key={i} style={{ border: '1px solid #E4E4E7', borderRadius: 12, padding: 12, background: '#FAFAFA', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <strong style={{ fontSize: '0.82rem', color: '#18181B' }}>اسلاید {i + 1}</strong>
              <button onClick={() => setSlides(slides.filter((_, x) => x !== i))}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#FEF2F2', color: '#991B1B', border: 'none', padding: '6px 12px', borderRadius: 8, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                <Trash2 size={12} /> حذف
              </button>
            </div>
            <FImage label="تصویر اسلاید" value={sl.image} onChange={v => setSlides(slides.map((x, xi) => xi === i ? { ...x, image: v } : x))} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
              <FText label="عنوان" value={sl.title} onChange={v => setSlides(slides.map((x, xi) => xi === i ? { ...x, title: v } : x))} placeholder="عنوان اسلاید" />
              <FText label="زیرعنوان" value={sl.subtitle} onChange={v => setSlides(slides.map((x, xi) => xi === i ? { ...x, subtitle: v } : x))} placeholder="توضیح کوتاه" />
              <FText label="لینک" value={sl.link} onChange={v => setSlides(slides.map((x, xi) => xi === i ? { ...x, link: v } : x))} placeholder="/category/slug" />
            </div>
          </div>
        ))}
        <button onClick={() => setSlides([...slides, { image: '', title: '', subtitle: '', link: '' }])}
          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: '#F4F4F5', color: '#18181B', border: '1px dashed #D4D4D8', padding: '10px', borderRadius: 12, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
          <Plus size={14} /> افزودن اسلاید
        </button>
      </div>
    );
  }

  if (type === 'faq') {
    const items = settings.items || [];
    const setItems = it => update('items', it);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <FText label="عنوان بخش" value={settings.sectionTitle} onChange={v => update('sectionTitle', v)} placeholder="سوالات متداول" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
          <FColor label="رنگ عنوان بخش" value={settings.titleColor} onChange={v => update('titleColor', v)} />
          <FNumber label="اندازه عنوان (px)" value={settings.titleSize} onChange={v => update('titleSize', v)} min={14} max={48} />
          <FColor label="رنگ پرسش" value={settings.qColor} onChange={v => update('qColor', v)} />
          <FNumber label="اندازه پرسش (px)" value={settings.qSize} onChange={v => update('qSize', v)} min={12} max={32} />
          <FColor label="رنگ پاسخ" value={settings.aColor} onChange={v => update('aColor', v)} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
            </div>
        {items.map((f, i) => (
          <div key={i} style={{ border: '1px solid #E4E4E7', borderRadius: 12, padding: 12, background: '#FAFAFA', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <strong style={{ fontSize: '0.82rem', color: '#18181B' }}>سوال {i + 1}</strong>
              <button onClick={() => setItems(items.filter((_, x) => x !== i))}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#FEF2F2', color: '#991B1B', border: 'none', padding: '6px 12px', borderRadius: 8, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                <Trash2 size={12} /> حذف
              </button>
            </div>
            <FText label="پرسش" value={f.q} onChange={v => setItems(items.map((x, xi) => xi === i ? { ...x, q: v } : x))} placeholder="سوال..." />
            <FArea label="پاسخ" value={f.a} onChange={v => setItems(items.map((x, xi) => xi === i ? { ...x, a: v } : x))} placeholder="پاسخ..." rows={3} />
          </div>
        ))}
        <button onClick={() => setItems([...items, { q: '', a: '' }])}
          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: '#F4F4F5', color: '#18181B', border: '1px dashed #D4D4D8', padding: '10px', borderRadius: 12, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
          <Plus size={14} /> افزودن سوال
        </button>
        <div style={{ fontSize: '0.75rem', color: '#A1A1AA' }}>اگر خالی باشد، سوالات صفحه «سوالات متداول» فروشگاه نمایش داده می‌شود.</div>
      </div>
    );
  }

  if (type === 'product_list' || type === 'featured') return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
      <FText label="عنوان بخش" value={settings.sectionTitle} onChange={v => update('sectionTitle', v)} placeholder={type === 'featured' ? 'پیشنهادات ویژه' : 'عنوان دلخواه'} />
      <FNumber label="تعداد محصولات" value={settings.limit ?? 6} onChange={v => update('limit', v)} min={1} max={24} />
      <CategorySelect value={settings.category_id === undefined || settings.category_id === null ? '' : String(settings.category_id)} onChange={v => update('category_id', v === '' ? undefined : Number(v))} />
      <div style={{ gridColumn: '1 / -1', borderTop: '1px dashed #E4E4E7', paddingTop: 10 }}>
        <TemplateSelect kind="product" value={settings.templateId} onChange={v => update('templateId', v === 'custom' ? undefined : v)} />
      </div>
      <div style={{ gridColumn: '1 / -1' }}>
        <ListTplEditor kind="product" value={settings.tpl || {}} onChange={v => update('tpl', v)} />
      </div>
      <div style={{ gridColumn: '1 / -1', borderTop: '1px dashed #E4E4E7', paddingTop: 10, background: '#F4F4F5', borderRadius: 12, padding: 16 }}>
        <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#18181B', marginBottom: 8 }}>پیش‌نمایش کارت (نمونه)</div>
        <ProductCard p={{ id: 1, name: 'نام نمونه محصول', description: 'توضیح کوتاه نمونه برای نمایش کارت در پیش‌نمایش ویجت.', stock: 12, customization_data: { base_price: 1250000 }, image_path: null, media: [] }} tpl={{ layout: 'grid', imageHeight: settings.tpl?.imageHeight || 190, imageFixedHeight: !!settings.tpl?.imageFixedHeight, image: settings.tpl?.image || 'top', shadow: settings.tpl?.shadow || 'md', radius: settings.tpl?.radius ?? 14, bg: settings.tpl?.bg || 'var(--card)' }} />
      </div>
    </div>
  );

  if (type === 'categories') return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
      <FText label="عنوان بخش" value={settings.sectionTitle} onChange={v => update('sectionTitle', v)} placeholder="دسته‌بندی محصولات" />
      <FColor label="رنگ عنوان" value={settings.titleColor} onChange={v => update('titleColor', v)} />
      <FNumber label="اندازه عنوان (px)" value={settings.titleSize} onChange={v => update('titleSize', v)} min={14} max={48} />
      <FSelect label="نوع چیدمان" value={settings.layout || 'grid'} onChange={v => update('layout', v)}
        options={[{ value: 'grid', label: 'منظم' }, { value: 'masonry', label: 'نامنظم' }]} />
      {settings.layout !== 'masonry' && (
        <FSelect label="تعداد ستون" value={String(settings.columns || 4)} onChange={v => update('columns', Number(v))}
          options={[{ value: '2', label: '۲ ستون' }, { value: '3', label: '۳ ستون' }, { value: '4', label: '۴ ستون' }, { value: '5', label: '۵ ستون' }]} />
      )}
    </div>
  );

  if (type === 'hero') return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
      <FText label="عنوان اصلی" value={settings.title} onChange={v => update('title', v)} placeholder="عنوان بنر" />
      <FColor label="رنگ عنوان" value={settings.titleColor} onChange={v => update('titleColor', v)} />
      <FNumber label="اندازه عنوان (px)" value={settings.titleSize} onChange={v => update('titleSize', v)} min={16} max={72} />
      <FText label="زیرعنوان" value={settings.subtitle} onChange={v => update('subtitle', v)} placeholder="توضیحات" />
      <FText label="متن دکمه" value={settings.buttonText} onChange={v => update('buttonText', v)} placeholder="خرید کنید" />
      <FText label="لینک دکمه" value={settings.buttonLink} onChange={v => update('buttonLink', v)} placeholder="/products" />
      <FColor label="رنگ زیرعنوان" value={settings.subtitleColor} onChange={v => update('subtitleColor', v)} />
    </div>
  );

  if (type === 'how_to_order') {
    const steps = Array.isArray(settings.steps) && settings.steps.length ? settings.steps : [
      { icon: 'Gem', title: 'انتخاب محصول', text: 'محصول مورد نظر را از کالکشن انتخاب کنید.' },
      { icon: 'PenTool', title: 'شخصی‌سازی', text: 'سایز، متن حکاکی و عکس خود را وارد کنید.' },
      { icon: 'Smartphone', title: 'ثبت شماره تماس', text: 'شماره موبایل را وارد و کد تایید پیامکی را دریافت کنید.' },
      { icon: 'Truck', title: 'ارسال و تحویل', text: 'آدرس را ثبت کرده و سفارش را درب منزل تحویل بگیرید.' },
    ];
    const setSteps = st => update('steps', st);
    return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
        <FText label="عنوان بخش" value={settings.sectionTitle} onChange={v => update('sectionTitle', v)} placeholder="روش سفارش" />
        <FText label="زیرعنوان (بالای عنوان)" value={settings.subtitle} onChange={v => update('subtitle', v)} placeholder="مثلاً: خرید در ۴ قدم" />
        <FText label="توضیح کوتاه (زیر عنوان)" value={settings.desc} onChange={v => update('desc', v)} placeholder="توضیح اختیاری..." />
        <FColor label="رنگ عنوان" value={settings.titleColor} onChange={v => update('titleColor', v)} />
        <FColor label="رنگ زیرعنوان" value={settings.subColor} onChange={v => update('subColor', v)} />
        <FNumber label="اندازه عنوان (px)" value={settings.titleSize} onChange={v => update('titleSize', v)} min={14} max={48} />
        <FSelect label="چیدمان" value={settings.layout || 'cards'} onChange={v => update('layout', v)}
          options={[{ value: 'cards', label: 'کارت‌ها (کنار هم)' }, { value: 'timeline', label: 'خط زمانی (زیر هم)' }]} />
        <FColor label="رنگ آیکون‌ها و خط" value={settings.accentColor} onChange={v => update('accentColor', v)} />
        <FColor label="پس‌زمینه کارت‌ها" value={settings.cardBg} onChange={v => update('cardBg', v)} />
      </div>
      {steps.map((st, i) => (
        <div key={i} style={{ border: '1px solid #E4E4E7', borderRadius: 12, padding: 12, background: '#FAFAFA', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <strong style={{ fontSize: '0.82rem', color: '#18181B' }}>مرحله {i + 1}</strong>
            <button onClick={() => setSteps(steps.filter((_, x) => x !== i))}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#FEF2F2', color: '#991B1B', border: 'none', padding: '6px 12px', borderRadius: 8, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              <Trash2 size={12} /> حذف
            </button>
          </div>
          <FIconPicker label="آیکون مرحله" value={st.icon} onChange={v => setSteps(steps.map((x, xi) => xi === i ? { ...x, icon: v } : x))} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
            <FText label="عنوان مرحله" value={st.title} onChange={v => setSteps(steps.map((x, xi) => xi === i ? { ...x, title: v } : x))} placeholder="عنوان مرحله" />
          </div>
          <FArea label="توضیح مرحله" value={st.text} onChange={v => setSteps(steps.map((x, xi) => xi === i ? { ...x, text: v } : x))} placeholder="توضیح کوتاه..." rows={2} />
        </div>
      ))}
      {steps.length < 6 && (
        <button onClick={() => setSteps([...steps, { icon: 'Sparkles', title: '', text: '' }])}
          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: '#F4F4F5', color: '#18181B', border: '1px dashed #D4D4D8', padding: '10px', borderRadius: 12, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
          <Plus size={14} /> افزودن مرحله
        </button>
      )}
    </div>
    );
  }

  if (type === 'icon') return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
      <FIconPicker label="آیکون" value={settings.icon} onChange={v => update('icon', v)} />
      <FNumber label="اندازه (px)" value={settings.size ?? 32} onChange={v => update('size', v)} min={12} max={120} />
      <FColor label="رنگ آیکون" value={settings.color} onChange={v => update('color', v)} />
      <FColor label="پس‌زمینه" value={settings.bg} onChange={v => update('bg', v)} />
      <FNumber label="گردی (px)" value={settings.radius} onChange={v => update('radius', v)} min={0} max={60} />
      <FSelect label="تراز" value={settings.align || 'center'} onChange={v => update('align', v)}
        options={[{ value: 'right', label: 'راست' }, { value: 'center', label: 'وسط' }, { value: 'left', label: 'چپ' }]} />
      <FText label="لینک (اختیاری)" value={settings.link} onChange={v => update('link', v)} placeholder="/products یا https://..." />
    </div>
  );

  if (type === 'icon_text') return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
      <FIconPicker label="آیکون" value={settings.icon} onChange={v => update('icon', v)} />
      <FText label="عنوان" value={settings.title} onChange={v => update('title', v)} placeholder="عنوان..." />
      <div style={{ gridColumn: '1 / -1' }}><FArea label="متن" value={settings.text} onChange={v => update('text', v)} placeholder="متن دلخواه..." rows={3} /></div>
      <FSelect label="چیدمان" value={settings.layout || 'top'} onChange={v => update('layout', v)}
        options={[{ value: 'top', label: 'آیکون بالا' }, { value: 'side', label: 'آیکون کنار' }]} />
      <FNumber label="اندازه آیکون (px)" value={settings.iconSize ?? 28} onChange={v => update('iconSize', v)} min={12} max={96} />
      <FColor label="رنگ آیکون" value={settings.iconColor} onChange={v => update('iconColor', v)} />
      <FColor label="پس‌زمینه آیکون" value={settings.iconBg} onChange={v => update('iconBg', v)} />
      <FColor label="رنگ عنوان" value={settings.titleColor} onChange={v => update('titleColor', v)} />
      <FNumber label="اندازه عنوان (px)" value={settings.titleSize} onChange={v => update('titleSize', v)} min={12} max={40} />
      <FColor label="رنگ متن" value={settings.textColor} onChange={v => update('textColor', v)} />
      <FSelect label="تراز" value={settings.align || 'center'} onChange={v => update('align', v)}
        options={[{ value: 'right', label: 'راست' }, { value: 'center', label: 'وسط' }, { value: 'left', label: 'چپ' }]} />
    </div>
  );

  if (type === 'divider') {
    const legacyLine = typeof settings.style === 'string' ? settings.style : 'solid';
    const setLine = v => {
      const next = { ...settings, lineStyle: v };
      if (typeof next.style === 'string') delete next.style;
      onChange(next);
    };
    const lineVal = settings.lineStyle || legacyLine;
    return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
      <FSelect label="مدل خط" value={lineVal} onChange={setLine}
        options={[{ value: 'solid', label: 'توپر' }, { value: 'dashed', label: 'خط‌چین' }, { value: 'dotted', label: 'نقطه‌چین' }, { value: 'double', label: 'دوبل' }, { value: 'gradient', label: 'محو (گرادیان)' }]} />
      <FColor label="رنگ خط" value={settings.color} onChange={v => update('color', v)} />
      <FNumber label="ضخامت (px)" value={settings.thickness ?? 1} onChange={v => update('thickness', v)} min={1} max={12} />
      <FNumber label="عرض (٪)" value={settings.width ?? 100} onChange={v => update('width', v)} min={5} max={100} />
      <FNumber label="فاصله عمودی (px)" value={settings.gap ?? 16} onChange={v => update('gap', v)} min={0} max={100} />
    </div>
  );
  }

  if (type === 'map') return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
      <FText label="آدرس (نمایش روی نقشه گوگل)" value={settings.address} onChange={v => update('address', v)} placeholder="تهران، خیابان ..." />
      <FText label="لینک embed دلخواه" value={settings.embedUrl} onChange={v => update('embedUrl', v)} placeholder="https://www.google.com/maps/embed?..." />
      <FNumber label="ارتفاع (px)" value={settings.height ?? 320} onChange={v => update('height', v)} min={160} max={800} />
      <FNumber label="گردی گوشه (px)" value={settings.radius ?? 16} onChange={v => update('radius', v)} min={0} max={48} />
    </div>
  );

  if (type === 'video') return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
      <div style={{ gridColumn: '1 / -1' }}>
        <FImage label="ویدیو (mp4/webm تا ۵۰MB)" value={settings.video} onChange={v => update('video', v)} accept="video/mp4,video/webm" kind="video" />
      </div>
      <FImage label="کاور (پوستر)" value={settings.poster} onChange={v => update('poster', v)} />
      <FNumber label="گردی گوشه (px)" value={settings.radius ?? 16} onChange={v => update('radius', v)} min={0} max={48} />
      <div>
        <label className={fieldLabelCls}>گزینه‌های پخش</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            ['controls', 'دکمه‌های کنترل', settings.controls !== false],
            ['autoplay', 'پخش خودکار', !!settings.autoplay],
            ['muted', 'بی‌صدا', !!settings.muted],
            ['loop', 'تکرار', !!settings.loop],
          ].map(([k, label, checked]) => (
            <label key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: '#52525B' }}>
              <input type="checkbox" checked={checked} onChange={e => update(k, e.target.checked)} style={{ accentColor: '#18181B', width: 16, height: 16 }} /> {label}
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  if (type === 'html') return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <label className={fieldLabelCls}>کد HTML سفارشی</label>
        <textarea value={settings.html || ''} onChange={e => update('html', e.target.value)} placeholder="<div>...</div>" rows={8}
          dir="ltr" className={fieldCls} style={{textAlign: 'left', fontFamily: 'monospace', resize: 'vertical', lineHeight: 1.7}} />
      </div>
      <div style={{ fontSize: '0.75rem', color: '#A1A1AA' }}>کد دقیقاً همان‌طور که هست در صفحه اصلی نمایش داده می‌شود.</div>
    </div>
  );

  if (type === 'rates') return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
      <FText label="عنوان" value={settings.sectionTitle} onChange={v => update('sectionTitle', v)} placeholder="نرخ لحظه‌ای نقره" />
      <FColor label="رنگ عنوان" value={settings.titleColor} onChange={v => update('titleColor', v)} />
      <FNumber label="اندازه عنوان (px)" value={settings.titleSize} onChange={v => update('titleSize', v)} min={14} max={48} />
      <FSelect label="نمایش مبدل" value={settings.converter || 'both'} onChange={v => update('converter', v)}
        options={[{ value: 'toman', label: 'تومان به گرم' }, { value: 'gram', label: 'گرم به تومان' }, { value: 'both', label: 'هر دو (زنده و دوطرفه)' }, { value: 'none', label: 'بدون مبدل' }]} />
      <RatesItemPicker value={settings.itemIds || []} onChange={v => update('itemIds', v)} />
      <FColor label="پس‌زمینه تب فعال" value={settings.tabActiveBg} onChange={v => update('tabActiveBg', v)} />
      <FColor label="رنگ متن تب فعال" value={settings.tabActiveColor} onChange={v => update('tabActiveColor', v)} />
      <FColor label="پس‌زمینه تب غیرفعال" value={settings.tabInactiveBg} onChange={v => update('tabInactiveBg', v)} />
      <FColor label="رنگ متن تب غیرفعال" value={settings.tabInactiveColor} onChange={v => update('tabInactiveColor', v)} />
      <FColor label="پس‌زمینه پنل" value={settings.panelBg} onChange={v => update('panelBg', v)} />
      <FColor label="حاشیه پنل" value={settings.panelBorder} onChange={v => update('panelBorder', v)} />
      <FColor label="رنگ قیمت" value={settings.priceColor} onChange={v => update('priceColor', v)} />
      <FCheck label="نمایش نمودار روند" checked={settings.showTrend !== false} onChange={v => update('showTrend', v)} />
      <FCheck label="افکت شیشه‌ای (glassmorphism)" checked={settings.glassStyle !== false} onChange={v => update('glassStyle', v)} />
    </div>
  );

  if (type === 'post_list') return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
      <FText label="عنوان بخش" value={settings.sectionTitle} onChange={v => update('sectionTitle', v)} placeholder="آخرین مطالب وبلاگ" />
      <FNumber label="تعداد مطالب" value={settings.limit ?? 6} onChange={v => update('limit', v)} min={1} max={24} />
      <div style={{ gridColumn: '1 / -1', borderTop: '1px dashed #E4E4E7', paddingTop: 10 }}>
        <TemplateSelect kind="post" value={settings.templateId} onChange={v => update('templateId', v === 'custom' ? undefined : v)} />
      </div>
      <div style={{ gridColumn: '1 / -1' }}>
        <ListTplEditor kind="post" value={settings.tpl || {}} onChange={v => update('tpl', v)} />
      </div>
    </div>
  );

  if (type === 'post_meta') return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
      <FColor label="رنگ عنوان مطلب" value={settings.titleColor} onChange={v => update('titleColor', v)} />
      <FNumber label="اندازه عنوان (px)" value={settings.titleSize} onChange={v => update('titleSize', v)} min={14} max={56} />
      <div>
        <label className={fieldLabelCls}>نمایش عنوان</label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: '#52525B' }}>
          <input type="checkbox" checked={settings.showTitle !== false} onChange={e => update('showTitle', e.target.checked)} style={{ accentColor: '#18181B', width: 16, height: 16 }} /> عنوان مطلب
        </label></div>
      <div>
        <label className={fieldLabelCls}>نمایش کاور</label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: '#52525B' }}>
          <input type="checkbox" checked={settings.showCover !== false} onChange={e => update('showCover', e.target.checked)} style={{ accentColor: '#18181B', width: 16, height: 16 }} /> تصویر شاخص
        </label></div>
      <div>
        <label className={fieldLabelCls}>نمایش تاریخ</label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: '#52525B' }}>
          <input type="checkbox" checked={settings.showDate !== false} onChange={e => update('showDate', e.target.checked)} style={{ accentColor: '#18181B', width: 16, height: 16 }} /> تاریخ انتشار
        </label></div>
      <div>
        <label className={fieldLabelCls}>نمایش خلاصه</label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: '#52525B' }}>
          <input type="checkbox" checked={settings.showExcerpt === true} onChange={e => update('showExcerpt', e.target.checked)} style={{ accentColor: '#18181B', width: 16, height: 16 }} /> متن خلاصه
        </label></div>
    </div>
  );

  if (type === 'post_comments') return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
      <FText label="عنوان باکس" value={settings.sectionTitle} onChange={v => update('sectionTitle', v)} placeholder="دیدگاه‌ها" />
    </div>
  );

  if (type === 'table') return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
      <div style={{ gridColumn: '1 / -1' }}><FText label="سرستون‌ها (با کاما جدا کنید)" value={settings.headers} onChange={v => update('headers', v)} placeholder="نام، قیمت، موجودی" /></div>
      <div style={{ gridColumn: '1 / -1' }}><FArea label="ردیف‌ها (هر خط یک ردیف، سلول‌ها با | جدا شوند)" value={settings.rows_text} onChange={v => update('rows_text', v)} placeholder="نام | قیمت | موجودی" rows={5} /></div>
      <FColor label="پس‌زمینه سرستون" value={settings.headerBg} onChange={v => update('headerBg', v)} />
      <FColor label="رنگ متن سرستون" value={settings.headerColor} onChange={v => update('headerColor', v)} />
      <FColor label="رنگ متن ردیف‌ها" value={settings.bodyColor} onChange={v => update('bodyColor', v)} />
      <FColor label="رنگ حاشیه" value={settings.borderColor} onChange={v => update('borderColor', v)} />
      <FColor label="رنگ خط جداکننده ردیف‌ها" value={settings.rowBorder} onChange={v => update('rowBorder', v)} />
      <FColor label="پس‌زمینه ردیف‌های یکی‌درمیان" value={settings.stripeBg} onChange={v => update('stripeBg', v)} />
      <FNumber label="اندازه فونت (px)" value={settings.fontSize} onChange={v => update('fontSize', v)} min={10} max={24} />
      <FSelect label="تراز متن" value={settings.align || 'right'} onChange={v => update('align', v)}
        options={[{ value: 'right', label: 'راست' }, { value: 'center', label: 'وسط' }, { value: 'left', label: 'چپ' }]} />
      <div>
        <label className={fieldLabelCls}>ردیف‌های راه‌راه</label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: '#52525B' }}>
          <input type="checkbox" checked={settings.striped !== false} onChange={e => update('striped', e.target.checked)} style={{ accentColor: '#18181B', width: 16, height: 16 }} /> پس‌زمینه یکی‌درمیان
        </label></div>
    </div>
  );

  if (type === 'product_scroll' || type === 'deal_row') return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
      <div style={{ gridColumn: '1 / -1' }}><FText label="عنوان بخش" value={settings.sectionTitle} onChange={v => update('sectionTitle', v)} placeholder={type === 'deal_row' ? 'تخفیف‌های محدود امروز' : 'محصولات فروشگاه'} /></div>
      {type === 'product_scroll' && (
        <FSelect label="منبع محصولات" value={settings.source || 'products'} onChange={v => update('source', v)}
          options={[{ value: 'products', label: 'جدیدترین محصولات' }, { value: 'featured', label: 'محصولات ویژه' }]} />
      )}
      {type === 'deal_row' && (
        <>
          <FText label="بج پنل" value={settings.badge} onChange={v => update('badge', v)} placeholder="پیشنهاد شگفت‌انگیز" />
          <div style={{ gridColumn: '1 / -1' }}><FText label="توضیح پنل" value={settings.desc} onChange={v => update('desc', v)} placeholder="توضیح کوتاه کمپین" /></div>
          <FText label="متن دکمه" value={settings.buttonText} onChange={v => update('buttonText', v)} placeholder="مشاهده تخفیف‌ها" />
        </>
      )}
      <FNumber label="تعداد آیتم‌ها" value={settings.limit} onChange={v => update('limit', v)} min={2} max={24} />
      <FNumber label="عرض کارت (px)" value={settings.cardWidth} onChange={v => update('cardWidth', v)} min={150} max={320} />
      <FText label="لینک مشاهده همه" value={settings.link} onChange={v => update('link', v)} placeholder="/products" />
      <FText label="متن لینک" value={settings.linkLabel} onChange={v => update('linkLabel', v)} placeholder="مشاهده همه" />
      <FCheck label="نمایش هدر" checked={settings.showHeader !== false} onChange={v => update('showHeader', v)} />
    </div>
  );

  if (type === 'slider_pro') {
    const slides = Array.isArray(settings.slides) ? settings.slides : [];
    const setSlide = (i, k, v) => { const next = slides.slice(); next[i] = { ...next[i], [k]: v }; update('slides', next); };
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
        <div style={{ gridColumn: '1 / -1' }}><FText label="عنوان بخش" value={settings.sectionTitle} onChange={v => update('sectionTitle', v)} placeholder="ویترین محصولات" /></div>
        <FSelect label="محتوای اسلایدر" value={settings.content || 'products'} onChange={v => update('content', v)}
          options={[{ value: 'products', label: 'محصولات' }, { value: 'categories', label: 'دسته‌بندی‌ها' }, { value: 'custom', label: 'اسلایدهای دستی' }]} />
        <FSelect label="سبک نمایش (استودیو)" value={settings.sliderMode || 'classic'} onChange={v => update('sliderMode', v)}
          options={[
            { value: 'classic', label: 'کلاسیک (فلش + نقطه)' },
            { value: 'coverflow', label: 'کاورفلو (کارت میانی بزرگ)' },
            { value: 'fade', label: 'محو شدن (تصویر بزرگ)' },
            { value: 'ticker', label: 'نوار متحرک پیوسته' },
            { value: 'story', label: 'استوری (متن روی تصویر)' },
            { value: 'thumbs', label: 'بندانگشتی (تصویر بزرگ + بندانگشت‌ها)' },
          ]} />
        {settings.content !== 'custom' && (
          <>
            {settings.content !== 'categories' && (
              <FSelect label="منبع محصولات" value={settings.source || 'products'} onChange={v => update('source', v)}
                options={[{ value: 'products', label: 'جدیدترین محصولات' }, { value: 'featured', label: 'محصولات ویژه' }]} />
            )}
            <FNumber label="تعداد آیتم‌ها" value={settings.limit} onChange={v => update('limit', v)} min={2} max={24} />
          </>
        )}
        <FNumber label="عرض کارت (px)" value={settings.cardWidth} onChange={v => update('cardWidth', v)} min={160} max={360} />
        <FNumber label="ارتفاع (px)" value={settings.height} onChange={v => update('height', v)} min={160} max={520} />
        <FNumber label="فاصله کارت‌ها (px)" value={settings.gap} onChange={v => update('gap', v)} min={6} max={32} />
        <FNumber label="گردی گوشه‌ها (px)" value={settings.radius} onChange={v => update('radius', v)} min={0} max={48} />
        <FNumber label="سرعت نوار متحرک (ثانیه)" value={settings.speed} onChange={v => update('speed', v)} min={12} max={80} />
        <FNumber label="فاصله پخش خودکار (ms)" value={settings.autoplayDelay} onChange={v => update('autoplayDelay', v)} min={1200} max={9000} />
        <FCheck label="پخش خودکار" checked={settings.autoplay !== false} onChange={v => update('autoplay', v)} />
        <FCheck label="فلش‌های جابه‌جایی" checked={settings.arrows !== false} onChange={v => update('arrows', v)} />
        <FCheck label="نقطه‌های پایین" checked={settings.dots !== false} onChange={v => update('dots', v)} />
        <FCheck label="پرده تیره روی تصویر" checked={settings.overlay !== false} onChange={v => update('overlay', v)} />
        <FText label="متن دکمه" value={settings.buttonText} onChange={v => update('buttonText', v)} placeholder="مشاهده" />
        <FText label="لینک هدر" value={settings.link} onChange={v => update('link', v)} placeholder="/products" />
        <FText label="متن لینک هدر" value={settings.linkLabel} onChange={v => update('linkLabel', v)} placeholder="مشاهده همه" />
        <FCheck label="نمایش هدر" checked={settings.showHeader !== false} onChange={v => update('showHeader', v)} />
        {settings.content === 'custom' && (
          <div style={{ gridColumn: '1 / -1', display: 'grid', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <strong style={{ fontSize: '0.85rem' }}>اسلایدهای دستی ({slides.length})</strong>
              <button type="button" onClick={() => update('slides', [...slides, { title: 'اسلاید جدید', text: '', image: '', link: '/products', buttonText: 'مشاهده' }])}
                style={{ padding: '7px 12px', borderRadius: 10, border: '1px solid #d4d4d8', background: '#fff', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>افزودن اسلاید</button>
            </div>
            {slides.map((sl, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, padding: 10, border: '1px dashed #d4d4d8', borderRadius: 12 }}>
                <FText label={`عنوان اسلاید ${i + 1}`} value={sl.title} onChange={v => setSlide(i, 'title', v)} />
                <FText label="متن کوتاه" value={sl.text} onChange={v => setSlide(i, 'text', v)} />
                <div style={{ gridColumn: '1 / -1' }}><FText label="آدرس عکس (آپلودی/URL)" value={sl.image} onChange={v => setSlide(i, 'image', v)} /></div>
                <FText label="لینک" value={sl.link} onChange={v => setSlide(i, 'link', v)} />
                <FText label="متن دکمه" value={sl.buttonText} onChange={v => setSlide(i, 'buttonText', v)} />
                <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8 }}>
                  <button type="button" onClick={() => { const nx = slides.slice(); const t = nx[i - 1]; if (t) { nx[i - 1] = nx[i]; nx[i] = t; update('slides', nx); } }}
                    style={{ padding: '5px 10px', borderRadius: 8, border: '1px solid #d4d4d8', background: '#fff', cursor: 'pointer', fontSize: '0.78rem' }}>جابه‌جایی ↑</button>
                  <button type="button" onClick={() => update('slides', slides.filter((_, j) => j !== i))}
                    style={{ padding: '5px 10px', borderRadius: 8, border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', cursor: 'pointer', fontSize: '0.78rem' }}>حذف</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (type === 'category_strip') return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
      <div style={{ gridColumn: '1 / -1' }}><FText label="عنوان بخش" value={settings.sectionTitle} onChange={v => update('sectionTitle', v)} placeholder="خرید بر اساس دسته‌بندی" /></div>
      <FSelect label="سبک نمایش" value={settings.variant || 'cards'} onChange={v => update('variant', v)} options={[{ value: 'cards', label: 'کارت‌دار' }, { value: 'circles', label: 'دایره‌ای (عکس بزرگ)' }, { value: 'plain', label: 'ساده بدون کادر' }]} />
      <FNumber label="تعداد دسته‌ها" value={settings.limit} onChange={v => update('limit', v)} min={3} max={24} />
      <FNumber label="عرض چیپ (px)" value={settings.cardWidth} onChange={v => update('cardWidth', v)} min={100} max={220} />
      <FText label="لینک مشاهده همه" value={settings.link} onChange={v => update('link', v)} placeholder="/products" />
      <FCheck label="نمایش هدر" checked={settings.showHeader !== false} onChange={v => update('showHeader', v)} />
    </div>
  );

  if (type === 'container') {
    const setInner = next => {
      const cur = settings.innerRows || [];
      update('innerRows', typeof next === 'function' ? next(cur) : next);
    };
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <FText label="عنوان کانتینر (اختیاری)" value={settings.sectionTitle} onChange={v => update('sectionTitle', v)} placeholder="" />
        <div style={{ border: '1px dashed #A1A1AA', borderRadius: 12, padding: 12, background: '#FAFAFA' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#78716c', marginBottom: 8 }}>ردیف‌های داخل کانتینر (۱ تا ۴ ستونه، هر ویجتی)</div>
          <HomeRowsEditor
            rows={settings.innerRows || []}
            setRows={setInner}
            types={WIDGET_TYPES.filter(t => t.type !== 'container')}
            isMobile={isMobileNested}
          />
        </div>
      </div>
    );
  }

  if (type === 'cart_widget') return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <FText label="عنوان ویجت" value={settings.sectionTitle} onChange={v => update('sectionTitle', v)} placeholder="سبد خرید" />
      <FCheck label="نمایش موجودی کیف پول" checked={settings.showWallet !== false} onChange={v => update('showWallet', v)} />
      <div style={{ fontSize: '0.78rem', color: '#A1A1AA' }}>ویجت سبد خرید به صورت خودکار از داده‌های کاربر خوانده و نمایش داده می‌شود. گزینه «نمایش موجودی کیف پول» موجودی کاربر را در باکس پرداخت نشان می‌دهد.</div>
    </div>
  );

  if (type === 'form') return <FormPickerEditor settings={settings} update={update} />;

  return null;
}

/* ---------- form picker for the form widget (which admin-designed form loads) ---------- */
function FormPickerEditor({ settings, update }) {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get('/forms').then(r => setForms(Array.isArray(r.data) ? r.data : [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  const sel = forms.find(f => String(f.id) === String(settings.form_id));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <label style={{ display: 'block', marginBottom: 6, fontSize: '0.78rem', fontWeight: 600, color: '#52525B', textAlign: 'right' }}>کدام فرم نمایش داده شود؟ *</label>
        <select value={settings.form_id || ''} onChange={e => update('form_id', e.target.value === '' ? '' : Number(e.target.value))}
          style={{ width: '100%', padding: '10px 14px', borderRadius: 12, border: '1.5px solid #E4E4E7', fontSize: '0.85rem', outline: 'none', background: '#FAFAFA', fontFamily: 'inherit', cursor: 'pointer' }}>
          <option value="">انتخاب کنید...</option>
          {forms.map(f => <option key={f.id} value={f.id}>{f.title} (#{f.id})</option>)}
        </select>
        {loading && <div style={{ fontSize: '0.75rem', color: '#A1A1AA', marginTop: 6 }}>در حال دریافت فرم‌ها...</div>}
        {!loading && !forms.length && <div style={{ fontSize: '0.78rem', color: '#A16207', background: '#FFFBEB', borderRadius: 10, padding: '8px 12px', marginTop: 8 }}>هنوز فرمی ساخته نشده — اول از بخش «فرم‌ساز» پنل ادمین یک فرم بسازید.</div>}
        {sel?.description && <div style={{ fontSize: '0.76rem', color: '#71717A', marginTop: 6 }}>{sel.description}</div>}
      </div>
    </div>
  );
}

/* ---------- every page widget = content settings + style settings (separated) ---------- */
export function HomeWidgetEditor({ type, settings, onChange }) {
  return (
    <WidgetShell settings={settings || {}} onChange={onChange}>
      <HomeWidgetContent type={type} settings={settings} onChange={onChange} />
    </WidgetShell>
  );
}

/* ================= shared rows engine ================= */
export const PLATFORMS = [
  { key: 'desktop', label: 'دسکتاپ', Icon: Monitor },
  { key: 'tablet', label: 'تبلت', Icon: Tablet },
  { key: 'mobile', label: 'موبایل', Icon: Smartphone },
];

export const migrateRows = arr => (arr || []).map(s => {
  if (s.columns) {
    return { id: s.id || uid(), enabled: s.enabled !== false, settings: s.settings || {}, columns: s.columns.map(c => ({ settings: c.settings || {}, widgets: (c.widgets || []).map(w => ({ id: w.id || uid(), enabled: w.enabled !== false, ...w })) })) };
  }
  return { id: uid(), enabled: s.enabled !== false, settings: {}, columns: [{ settings: {}, widgets: [{ id: uid(), enabled: s.enabled !== false, type: s.type, settings: s.settings || {} }] }] };
});

/* پاک‌سازی ردیف‌ها برای ذخیره — هر سه استودیو (صفحه اصلی/هدر-فوتر/برگه) یکسان */
export const cleanRows = list => (list || []).map(row => ({
  id: row.id, enabled: row.enabled, settings: row.settings || {}, columns: row.columns,
}));

/* ===== همگام‌سازی روشن/خاموش کردن بین همه نسخه‌های دستگاه (دسکتاپ/تبلت/موبایل) =====
 * «خاموش» یک تصمیم محتوایی است و باید روی همه دستگاه‌ها اعمال شود؛
 * تطبیق ابتدا با id و سپس با امضای محتوا (نوع + تیتر) تا کپی‌های هم‌نام هم هماهنگ شوند. */
const wSig = w => `${w.type || ''}|${(w.settings && (w.settings.sectionTitle ?? w.settings.title ?? w.settings.text ?? w.settings.name)) ?? ''}`;
const rSig = r => (r.columns || []).map(c => (c.widgets || []).map(wSig).join('§')).join('‖');

const collectFlags = (rows, into) => {
  const push = (key, enabled) => { if (key) into[key] = enabled; };
  (rows || []).forEach(r => {
    push('row:' + r.id, r.enabled !== false);
    push('row:' + rSig(r), r.enabled !== false);
    (r.columns || []).forEach(c => (c.widgets || []).forEach(w => {
      push('w:' + w.id, w.enabled !== false);
      push('w:' + wSig(w), w.enabled !== false);
      if (Array.isArray(w.settings && w.settings.innerRows)) collectFlags(w.settings.innerRows, into);
    }));
  });
  return into;
};

const applyFlags = (rows, flags) => (rows || []).map(r => {
  const re = flags['row:' + r.id] ?? flags['row:' + rSig(r)];
  const nr = (re !== undefined && (r.enabled !== false) !== re) ? { ...r, enabled: re } : r;
  return {
    ...nr,
    columns: (nr.columns || []).map(c => ({
      ...c,
      widgets: (c.widgets || []).map(w => {
        const we = flags['w:' + w.id] ?? flags['w:' + wSig(w)];
        let nw = (we !== undefined && (w.enabled !== false) !== we) ? { ...w, enabled: we } : w;
        if (Array.isArray(nw.settings && nw.settings.innerRows)) {
          nw = { ...nw, settings: { ...nw.settings, innerRows: applyFlags(nw.settings.innerRows, flags) } };
        }
        return nw;
      }),
    })),
  };
});

/** هر تغییر روشن/خاموش در نسخه‌ی فعلی، روی بقیه دستگاه‌ها هم اعمال می‌شود. */
export function syncEnableFlags(all, tab, nextRows) {
  const prevFlags = collectFlags(all[tab] || [], {});
  const nextFlags = collectFlags(nextRows, {});
  const changed = {};
  Object.keys(nextFlags).forEach(k => {
    if (prevFlags[k] !== undefined && prevFlags[k] !== nextFlags[k]) changed[k] = nextFlags[k];
  });
  const out = { ...all, [tab]: nextRows };
  if (Object.keys(changed).length) {
    PLATFORMS.forEach(({ key }) => {
      if (key !== tab) out[key] = applyFlags(all[key] || [], changed);
    });
  }
  return out;
}

export function HomeRowsEditor({ rows, setRows, types, isMobile, renderPreview, previewTitle }) {
  const [openPicker, setOpenPicker] = useState(null); // {r,c}
  const [dropTarget, setDropTarget] = useState(null); // {r,c,i?}
  const [cfgWidget, setCfgWidget] = useState(null); // {r,c,i,tab} — widget settings modal
  const [cfgRow, setCfgRow] = useState(null); // row index — row settings modal
  const [cfgCol, setCfgCol] = useState(null); // {r,c} — column style modal
  const [menuOpen, setMenuOpen] = useState(null); // widget card ⋯ menu key
  const dragPayload = useRef(null);

  /* ----- row ops ----- */
  const addRow = cols => {
    setRows([...rows, { id: uid(), enabled: true, settings: {}, columns: Array.from({ length: cols }, () => ({ widgets: [], settings: {} })) }]);
  };
  const moveRow = (r, dir) => {
    const j = r + dir;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[r], next[j]] = [next[j], next[r]];
    setRows(next);
  };
  const deleteRow = r => { setRows(rows.filter((_, x) => x !== r)); setCfgRow(null); };
  const toggleRow = r => setRows(rows.map((row, x) => x === r ? { ...row, enabled: !row.enabled } : row));
  const updateRowSettings = (r, settings) => setRows(rows.map((row, x) => x === r ? { ...row, settings } : row));
  const updateColumnSettings = (r, c, settings) => setRows(rows.map((row, x) => x !== r ? row : {
    ...row, columns: row.columns.map((col, y) => y !== c ? col : { ...col, settings }),
  }));
  const setRowCols = (r, cols) => {
    setRows(rows.map((row, x) => {
      if (x !== r) return row;
      const flat = row.columns.flatMap(c => c.widgets);
      const next = Array.from({ length: cols }, (_, i) => ({ widgets: [], settings: row.columns[i]?.settings || {} }));
      flat.forEach((w, i) => next[Math.min(i, cols - 1)].widgets.push(w));
      return { ...row, columns: next };
    }));
  };

  /* ----- widget ops ----- */
  const addWidget = (r, c, type) => {
    setRows(rows.map((row, x) => x !== r ? row : {
      ...row,
      columns: row.columns.map((col, y) => y !== c ? col : { widgets: [...col.widgets, { id: uid(), enabled: true, type, settings: {} }] }),
    }));
    setOpenPicker(null);
    setCfgWidget({ r, c, i: rows[r].columns[c].widgets.length, tab: 'content' });
  };
  const deleteWidget = (r, c, i) => {
    setRows(rows.map((row, x) => x !== r ? row : {
      ...row,
      columns: row.columns.map((col, y) => y !== c ? col : { widgets: col.widgets.filter((_, z) => z !== i) }),
    }));
    setCfgWidget(null);
  };
  const duplicateWidget = (r, c, i) => {
    setRows(rows.map((row, x) => x !== r ? row : {
      ...row,
      columns: row.columns.map((col, y) => y !== c ? col : {
        widgets: [...col.widgets.slice(0, i + 1), { ...JSON.parse(JSON.stringify(col.widgets[i])), id: uid() }, ...col.widgets.slice(i + 1)],
      }),
    }));
  };
  const moveWidget = (r, c, i, dir) => {
    const col = rows[r].columns[c];
    const j = i + dir;
    if (j < 0 || j >= col.widgets.length) return;
    const next = [...rows];
    const ws = [...col.widgets];
    [ws[i], ws[j]] = [ws[j], ws[i]];
    next[r] = { ...next[r], columns: next[r].columns.map((cc, y) => y === c ? { widgets: ws } : cc) };
    setRows(next);
  };
  const updateWidgetSettings = (r, c, i, settings) => {
    setRows(rows.map((row, x) => x !== r ? row : {
      ...row,
      columns: row.columns.map((col, y) => y !== c ? col : { widgets: col.widgets.map((w, z) => z === i ? { ...w, settings } : w) }),
    }));
  };

  const toggleWidget = (r, c, i) => {
    setRows(rows.map((row, x) => x !== r ? row : {
      ...row,
      columns: row.columns.map((col, y) => y !== c ? col : { ...col, widgets: col.widgets.map((w, z) => z === i ? { ...w, enabled: w.enabled === false } : w) }),
    }));
  };

  /* ----- drag & drop (desktop) ----- */
  const insertPayload = (r, c, at) => {
    const p = dragPayload.current;
    if (!p) return;
    const next = JSON.parse(JSON.stringify(rows));
    let widget;
    if (p.kind === 'new') {
      widget = { id: uid(), enabled: true, type: p.type, settings: {} };
    } else {
      const [w] = next[p.r].columns[p.c].widgets.splice(p.i, 1);
      widget = w;
      // adjust target index when moving inside same column from above
      if (p.r === r && p.c === c && at !== undefined && p.i < at) at -= 1;
    }
    const col = next[r].columns[c].widgets;
    col.splice(at === undefined ? col.length : at, 0, widget);
    setRows(next);
    dragPayload.current = null;
    setDropTarget(null);
  };

  /* ----- modal lookups ----- */
  const cfgW = cfgWidget ? rows[cfgWidget.r]?.columns[cfgWidget.c]?.widgets[cfgWidget.i] : null;
  const cfgWType = cfgWidget ? types.find(t => t.type === cfgW?.type) : null;
  const cfgRowData = cfgRow !== null ? rows[cfgRow] : null;
  const cfgColData = cfgCol ? rows[cfgCol.r]?.columns[cfgCol.c] : null;
  const cfgGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 };

  /* ----- rows canvas (add-row buttons + rows): the RIGHT-side editor pane ----- */
  const editor = (
    <div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#A1A1AA', marginBottom: 8, letterSpacing: 0.3 }}>افزودن ردیف جدید</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[1, 2, 3, 4].map(n => (
              <button key={n} onClick={() => addRow(n)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#F4F4F5', color: '#18181B', border: 'none', padding: '8px 14px', borderRadius: 10, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                <span style={{ display: 'inline-flex', gap: 2 }}>
                  {Array.from({ length: n }).map((_, k) => <span key={k} style={{ width: 10, height: 14, borderRadius: 2, background: '#A1A1AA', display: 'inline-block' }} />)}
                </span>
                {n === 1 ? 'تک‌ستونه' : `${n} ستونه`}
              </button>
            ))}
          </div>
        </div>

        {/* rows canvas */}
        <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 10, background: '#F0FDF4', border: '1px solid #BBF7D0', fontSize: '0.75rem', color: '#166534' }}>
          راهنما: بخش‌های خاموش اینجا کمرنگ دیده می‌شوند تا دوباره روشن‌شان کنید — ولی در سایت واقعی کاملاً مخفی‌اند.
        </div>
        {rows.length === 0 ? (
          <div style={{ padding: 28, textAlign: 'center', color: '#A1A1AA', fontSize: '0.9rem', border: '1px solid #E4E4E7', borderRadius: 14, background: '#FAFAFA' }}>
            ردیفی ساخته نشده است. ابتدا یک ردیف اضافه کنید، سپس داخل هر ستون ویجت بگذارید.
          </div>
        ) : rows.map((row, r) => {
          const cols = row.columns.length;
          return (
            <div key={row.id || r} style={{ borderRadius: 16, border: '1px solid #E4E4E7', background: '#fff', marginBottom: 20, overflow: 'hidden', opacity: row.enabled ? 1 : 0.55, transition: 'opacity 0.15s' }}>
              {/* row header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', background: '#FAFAFA', borderBottom: '1px solid #F4F4F5', flexWrap: 'wrap' }}>
                <LayoutGrid size={15} style={{ color: '#A1A1AA', flexShrink: 0 }} />
                <strong style={{ fontSize: '0.85rem', color: '#18181B' }}>ردیف {r + 1} · {cols} ستونه</strong>
                <span style={{ flex: 1 }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <button onClick={() => moveRow(r, -1)} title="ردیف بالاتر" style={iconBtn}><ArrowUp size={14} /></button>
                  <button onClick={() => moveRow(r, 1)} title="ردیف پایین‌تر" style={iconBtn}><ArrowDown size={14} /></button>
                  <button onClick={() => setCfgRow(r)} title="تنظیمات ردیف"
                    style={{ ...iconBtn, background: '#fff', color: '#52525B', borderColor: '#E4E4E7' }}>
                    <Settings2 size={14} />
                  </button>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: row.enabled ? '#F0FDF4' : '#F4F4F5', color: row.enabled ? '#166534' : '#A1A1AA', border: 'none', padding: '6px 12px', borderRadius: 8, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}>
                    <input type="checkbox" checked={!!row.enabled} onChange={() => toggleRow(r)} style={{ accentColor: '#166534' }} />
                    {row.enabled ? 'فعال' : 'غیرفعال'}
                  </label>
                  <button onClick={() => deleteRow(r)} title="حذف ردیف"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#FEF2F2', color: '#991B1B', border: 'none', padding: '6px 12px', borderRadius: 8, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                    <Trash2 size={13} /> حذف
                  </button>
                </div>
              </div>

              {/* columns — stacked vertically in the editor for room (frontend layout is untouched; see live preview) */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16, padding: 16, background: row.settings.bg && row.settings.bg !== 'transparent' ? row.settings.bg : undefined, borderRadius: radiusCss(row.settings), boxShadow: SHADOWS[row.settings.shadow], overflow: (radiusCss(row.settings) || row.settings.shadow) ? 'hidden' : undefined, margin: `${row.settings.marginY || 0}px 0` }}>
                {row.columns.map((col, c) => {
                  const cs = col.settings || {};
                  const isDrop = dropTarget && dropTarget.r === r && dropTarget.c === c && dropTarget.i === undefined;
                  return (
                    <div key={c}
                      onDragOver={!isMobile ? e => { e.preventDefault(); setDropTarget({ r, c }); } : undefined}
                      onDragLeave={!isMobile ? () => setDropTarget(null) : undefined}
                      onDrop={!isMobile ? e => { e.preventDefault(); e.stopPropagation(); insertPayload(r, c); } : undefined}
                      style={{ border: isDrop ? '1.5px dashed #18181B' : '1.5px dashed #E4E4E7', borderRadius: radiusCss(cs) ?? 12, padding: 12 + (cs.padding || 0), margin: cs.margin || 0, boxShadow: SHADOWS[cs.shadow], overflow: radiusCss(cs) ? 'hidden' : undefined, background: isDrop ? '#F5F3F0' : (cs.bg && cs.bg !== 'transparent' ? cs.bg : '#FAFAFA'), minHeight: 90, minWidth: 0, transition: 'border-color 0.15s, background 0.15s' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#D4D4D8', flexShrink: 0 }} />
                        <span style={{ fontSize: '0.74rem', fontWeight: 700, color: '#71717A' }}>ستون {c + 1}{cols > 1 ? ` از ${cols}` : ''}</span>
                        <span style={{ flex: 1 }} />
                        <button onClick={() => setCfgCol({ r, c })} title="استایل ستون"
                          style={{ ...iconBtn, width: 28, height: 28, background: '#fff', color: '#A1A1AA', borderColor: '#E4E4E7' }}>
                          <Settings2 size={13} />
                        </button>
                      </div>
                      {col.widgets.map((w, i) => {
                        const wt = types.find(t => t.type === w.type);
                        const over = dropTarget && dropTarget.r === r && dropTarget.c === c && dropTarget.i === i;
                        const menuKey = `${r}-${c}-${i}`;
                        const styleOn = !isStyleEmpty(w.settings?.style);
                        return (
                          <React.Fragment key={w.id || i}>
                            {over && <div style={{ height: 3, background: '#18181B', borderRadius: 4, margin: '4px 0' }} />}
                            <div
                              draggable={!isMobile}
                              onDragStart={!isMobile ? e => { e.stopPropagation(); dragPayload.current = { kind: 'move', r, c, i }; e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', 'move'); } : undefined}
                              onDragOver={!isMobile ? e => { e.preventDefault(); e.stopPropagation(); setDropTarget({ r, c, i }); } : undefined}
                              onDrop={!isMobile ? e => { e.preventDefault(); e.stopPropagation(); insertPayload(r, c, i); } : undefined}
                              onDragEnd={!isMobile ? () => { dragPayload.current = null; setDropTarget(null); } : undefined}
                              onClick={() => setCfgWidget({ r, c, i, tab: 'content' })}
                              style={{ border: '1px solid #E4E4E7', background: '#fff', borderRadius: 12, padding: '10px 12px', marginBottom: 10, cursor: 'pointer', opacity: w.enabled === false ? 0.55 : 1, transition: 'border-color 0.15s, box-shadow 0.15s' }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = '#A1A1AA'; e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.06)'; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = '#E4E4E7'; e.currentTarget.style.boxShadow = 'none'; }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {!isMobile && <GripVertical size={15} style={{ color: '#D4D4D8', cursor: 'grab', flexShrink: 0 }} onClick={e => e.stopPropagation()} />}
                                <span style={{ display: 'inline-flex', color: '#52525B' }}><TypeIcon type={w.type} size={18} /></span>
                                <span style={{ flex: 1, minWidth: 0 }}>
                                  <strong style={{ display: 'block', fontSize: '0.82rem', color: '#18181B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{wt?.label || w.type}</strong>
                                  {wt?.desc && <span style={{ display: 'block', fontSize: '0.68rem', color: '#A1A1AA', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{wt.desc}</span>}
                                </span>
                                {styleOn && <span title="استایل فعال" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.64rem', fontWeight: 800, color: '#166534', background: '#F0FDF4', borderRadius: 20, padding: '2px 9px', flexShrink: 0 }}><Palette size={11} /> استایل</span>}
                                {w.enabled === false && <span style={{ fontSize: '0.64rem', fontWeight: 800, color: '#A1A1AA', background: '#F4F4F5', borderRadius: 20, padding: '2px 9px', flexShrink: 0 }}>خاموش</span>}
                                <span style={{ display: 'flex', gap: 6, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                                  <button onClick={() => setCfgWidget({ r, c, i, tab: 'content' })} title="تنظیم محتوا"
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#18181B', color: '#fff', border: 'none', padding: '7px 13px', borderRadius: 9, fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                                    <FileText size={13} /> متن
                                  </button>
                                  <button onClick={() => setCfgWidget({ r, c, i, tab: 'style' })} title="تنظیم استایل"
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: styleOn ? '#F0FDF4' : '#fff', color: styleOn ? '#166534' : '#52525B', border: styleOn ? '1px solid #BBF7D0' : '1px solid #E4E4E7', padding: '7px 13px', borderRadius: 9, fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                                    <Palette size={13} /> استایل
                                  </button>
                                  <span style={{ position: 'relative', display: 'inline-flex' }}>
                                    <button onClick={() => setMenuOpen(menuOpen === menuKey ? null : menuKey)} title="اقدامات بیشتر"
                                      style={{ ...iconBtn, width: 32, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><MoreHorizontal size={15} /></button>
                                    <CardMenu open={menuOpen === menuKey} onClose={() => setMenuOpen(null)} items={[
                                      { icon: <ArrowUp size={14} />, label: 'انتقال به بالا', onClick: () => moveWidget(r, c, i, -1) },
                                      { icon: <ArrowDown size={14} />, label: 'انتقال به پایین', onClick: () => moveWidget(r, c, i, 1) },
                                      { icon: <Copy size={14} />, label: 'تکثیر ویجت', onClick: () => duplicateWidget(r, c, i) },
                                      { icon: w.enabled === false ? <Eye size={14} /> : <EyeOff size={14} />, label: w.enabled === false ? 'روشن کردن' : 'خاموش کردن', onClick: () => toggleWidget(r, c, i) },
                                      { icon: <Trash2 size={14} />, label: 'حذف ویجت', danger: true, onClick: () => deleteWidget(r, c, i) },
                                    ]} />
                                  </span>
                                </span>
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      })}
                      {/* add widget */}
                      <button onClick={() => setOpenPicker({ r, c })}
                        style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#fff', color: '#52525B', border: '1.5px dashed #D4D4D8', padding: '12px', borderRadius: 12, fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'border-color 0.15s, color 0.15s' }}>
                        <Plus size={15} /> افزودن ویجت به این ستون
                      </button>
                      {openPicker && openPicker.r === r && openPicker.c === c && (
                        <WidgetPicker types={types} onPick={t => addWidget(r, c, t)} onClose={() => setOpenPicker(null)} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      {/* ----- settings modals (compact, always in view) ----- */}
      {cfgWidget && cfgW && (
        <WidgetSettingsModal
          icon={<TypeIcon type={cfgW.type} size={20} />}
          title={`ویجت «${cfgWType?.label || cfgW.type}»`}
          desc={cfgWType?.desc || 'محتوا و استایل این ویجت را جداگانه تنظیم کنید.'}
          tab={cfgWidget.tab} setTab={t => setCfgWidget({ ...cfgWidget, tab: t })}
          styleActive={!isStyleEmpty(cfgW.settings?.style)}
          onClose={() => setCfgWidget(null)}
          content={<HomeWidgetContent type={cfgW.type} settings={cfgW.settings || {}} onChange={s => updateWidgetSettings(cfgWidget.r, cfgWidget.c, cfgWidget.i, s)} />}
          style={<WidgetStyleSection value={cfgW.settings?.style} defaultOpen onChange={v => updateWidgetSettings(cfgWidget.r, cfgWidget.c, cfgWidget.i, { ...(cfgW.settings || {}), style: v })} />}
        />
      )}
      {cfgRowData && (
        <AdminModal title={`تنظیمات ردیف ${cfgRow + 1}`} icon={<Rows3 size={18} />} desc="ساختار و ظاهر این ردیف" onClose={() => setCfgRow(null)}>
          <div style={cfgGrid}>
            <FColor label="پس‌زمینه ردیف" value={cfgRowData.settings.bg} onChange={v => updateRowSettings(cfgRow, { ...cfgRowData.settings, bg: v })} />
            <SidesFields v={cfgRowData.settings} onChange={s => updateRowSettings(cfgRow, s)} prefix="pad" title="فاصله داخلی ردیف از چهار طرف (px)" />
            <FNumber label="فاصله ستون‌ها (px)" value={cfgRowData.settings.gap ?? 16} onChange={v => updateRowSettings(cfgRow, { ...cfgRowData.settings, gap: v })} min={0} max={64} />
            <FSelect label="تعداد ستون" value={String(cfgRowData.columns.length)} onChange={v => setRowCols(cfgRow, Number(v))}
              options={[{ value: '1', label: '۱ ستون' }, { value: '2', label: '۲ ستون' }, { value: '3', label: '۳ ستون' }, { value: '4', label: '۴ ستون' }]} />
            <BoxStyleFields v={cfgRowData.settings} onChange={s => updateRowSettings(cfgRow, s)} marginKey="marginY" marginLabel="فاصله بیرونی بالا/پایین (px)" />
          </div>
        </AdminModal>
      )}
      {cfgColData && (
        <AdminModal title="استایل ستون" icon={<Columns3 size={18} />} desc="ظاهر این ستون" onClose={() => setCfgCol(null)}>
          <div style={cfgGrid}>
            <FColor label="پس‌زمینه ستون" value={cfgColData.settings?.bg} onChange={v => updateColumnSettings(cfgCol.r, cfgCol.c, { ...(cfgColData.settings || {}), bg: v })} />
            <FNumber label="فاصله داخلی (px)" value={cfgColData.settings?.padding} onChange={v => updateColumnSettings(cfgCol.r, cfgCol.c, { ...(cfgColData.settings || {}), padding: v })} min={0} max={60} />
            <BoxStyleFields v={cfgColData.settings || {}} onChange={s => updateColumnSettings(cfgCol.r, cfgCol.c, s)} marginKey="margin" marginLabel="فاصله بیرونی (px)" />
          </div>
        </AdminModal>
      )}
      </div>
  );

  /* ----- no preview renderer → legacy single-column editor (backward compatible) ----- */
  if (!renderPreview) return editor;

  return (
    <EditorStudio
      editor={editor}
      preview={renderPreview(rows)}
      previewTitle={previewTitle}
    />
  );
}

/* ================= two-pane studio: widgets RIGHT, live preview LEFT =================
 * Shared shell (pages, header/footer, blog): device switch (desktop/tablet/mobile),
 * sticky live preview, fullscreen preview, stacked edit/preview tabs on mobile. */
export function EditorStudio({ editor, preview, previewTitle }) {
  const [device, setDevice] = useState('desktop');
  const [full, setFull] = useState(false);
  const [mobileView, setMobileView] = useState('edit');
  const isMobileScreen = typeof window !== 'undefined' && window.innerWidth < 900;
  const widths = { desktop: '100%', tablet: 768, mobile: 390 };
  const deviceBtn = (key, Icon, label) => (
    <button key={key} onClick={() => setDevice(key)} title={label}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: device === key ? '#18181B' : '#fff', color: device === key ? '#fff' : '#52525B', border: '1px solid #E4E4E7', padding: '7px 14px', borderRadius: 10, fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
      <Icon size={14} /> {label}
    </button>
  );
  const previewBox = (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <div style={{ width: widths[device], maxWidth: '100%', border: device === 'desktop' ? 'none' : '1px solid #E4E4E7', borderRadius: device === 'desktop' ? 0 : 14, overflow: 'hidden', background: '#fff', transition: 'width 0.25s' }}>
        {device !== 'desktop' && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px 0', borderBottom: '1px solid #F4F4F5' }}>
            <span style={{ width: 60, height: 5, borderRadius: 3, background: '#E4E4E7' }} />
          </div>
        )}
        <div style={{ padding: device === 'mobile' ? 8 : 12 }}>
          {preview}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        {deviceBtn('desktop', Monitor, 'دسکتاپ')}
        {deviceBtn('tablet', Tablet, 'تبلت')}
        {deviceBtn('mobile', Smartphone, 'موبایل')}
        <span style={{ flex: 1 }} />
        <button onClick={() => setFull(true)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', color: '#18181B', border: '1px solid #E4E4E7', padding: '7px 14px', borderRadius: 10, fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
          <Maximize2 size={14} /> پیش‌نمایش تمام‌صفحه
        </button>
      </div>
      {isMobileScreen && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {[['edit', 'ویرایش ویجت‌ها'], ['preview', 'پیش‌نمایش زنده']].map(([k, label]) => (
            <button key={k} onClick={() => setMobileView(k)}
              style={{ flex: 1, padding: '9px', borderRadius: 10, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.82rem', fontWeight: 700, background: mobileView === k ? '#18181B' : '#F4F4F5', color: mobileView === k ? '#fff' : '#52525B' }}>
              {label}
            </button>
          ))}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: isMobileScreen ? '1fr' : 'minmax(440px, 5fr) 7fr', gap: 20, alignItems: 'start' }}>
        <div style={{ display: isMobileScreen && mobileView !== 'edit' ? 'none' : 'block', minWidth: 0 }}>
          {editor}
        </div>
        <div style={{ display: isMobileScreen && mobileView !== 'preview' ? 'none' : 'block', minWidth: 0, position: isMobileScreen ? 'static' : 'sticky', top: 16 }}>
          <div style={{ background: '#FAFAFA', border: '1px solid #E4E4E7', borderRadius: 16, padding: 14 }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#18181B', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Eye size={14} /> پیش‌نمایش زنده{previewTitle ? ` — ${previewTitle}` : ''} <span style={{ color: '#A1A1AA', fontWeight: 400 }}>(نسخه {device === 'desktop' ? 'دسکتاپ' : device === 'tablet' ? 'تبلت' : 'موبایل'})</span>
            </div>
            {previewBox}
          </div>
        </div>
      </div>
      {full && (
        <div onClick={() => setFull(false)} role="dialog" aria-modal="true"
          style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(12,10,9,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, direction: 'rtl', overflowY: 'auto' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 1000, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ position: 'sticky', top: 0, zIndex: 5, background: '#18181B', color: '#fff', padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 10, borderRadius: '20px 20px 0 0' }}>
              <span style={{ fontWeight: 800, fontSize: '0.9rem', flex: 1, display: 'inline-flex', alignItems: 'center', gap: 8 }}><Eye size={16} /> پیش‌نمایش کلی{previewTitle ? ` — ${previewTitle}` : ''}</span>
              <div style={{ display: 'flex', gap: 6 }}>
                {deviceBtn('desktop', Monitor, '')}
                {deviceBtn('tablet', Tablet, '')}
                {deviceBtn('mobile', Smartphone, '')}
              </div>
              <button onClick={() => setFull(false)} aria-label="بستن"
                style={{ width: 34, height: 34, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><X size={16} /></button>
            </div>
            <div style={{ padding: 12 }}>{previewBox}</div>
          </div>
        </div>
      )}
    </div>
  );
}
